import { motion } from 'motion/react';
import { forwardRef, useState } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { isProd } from '../../../env';
import { MarkdownTypewriter } from '../../Markdown/MarkdownTypewriter';
import { LoadingThreeDotsPulse } from '../../ui/loaders/three-dots';

import { generateMarkdownComponents } from './MarkdownComponents';
import styles from './styles.module.css';

import type { ChatMessage } from '../../../types/chat';

interface MessageProps {
  message?: ChatMessage;
  isLastMessage?: boolean;
}

const components = generateMarkdownComponents();

// TODO: display next message when the last message is finished typing. also animate the transition. but first memoize
const ChatMessageBubble = forwardRef<HTMLDivElement, MessageProps>(({ message, isLastMessage }, fwdRef) => {
  const isAI = !message || message.type === 'ai';
  const [isTypingComplete, setIsTypingComplete] = useState(!isAI);

  return message ? (
    <>
      <div
        ref={(ref) => {
          if (!fwdRef || !isAI || !isLastMessage) return;
          if (typeof fwdRef === 'function') {
            fwdRef(ref);
          } else if (fwdRef) {
            (fwdRef as React.MutableRefObject<HTMLDivElement | null>).current = ref;
          }
        }}
        className={`${styles.messageContainer} ${isAI ? styles.aiMessage : styles.userMessage}`}
        data-message-type={message.type}
        data-message-id={message.id}
        style={{ marginLeft: isAI ? 0 : 16, marginRight: isAI ? 16 : 0 }}
      >
        {isAI && isLastMessage ? (
          <MarkdownTypewriter
            remarkPlugins={[remarkGfm]}
            motionProps={{
              onAnimationComplete: () => {
                setIsTypingComplete(true);
                if (!isProd) {
                  console.log('Typing complete for message:', message.id, isTypingComplete);
                  // don't want to delete this state for now, so just logging it
                }
              },
            }}
          >
            {message.content}
          </MarkdownTypewriter>
        ) : (
          <Markdown components={components}>{message.content}</Markdown>
        )}
      </div>
      {/* {isAI && isTypingComplete && <MessageFeedback messageId={message.id} content={message.content} />} */}
    </>
  ) : (
    <motion.div
      className={`${styles.messageContainer} ${isAI ? styles.aiMessage : styles.userMessage}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <LoadingThreeDotsPulse />
    </motion.div>
  );
});

ChatMessageBubble.displayName = 'ChatMessageBubble';

export { ChatMessageBubble };
